import React from 'react';
import svgPaths from "../imports/svg-fcyeews8dc";
import { DEFAULT_PLACEHOLDER } from '../../../utils/assetUtils';

import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { setSelectedTemplate } from '../../../features/templates/templatesSlice';
import { useGetSavedTemplatesQuery, SavedDesign, useDeleteSavedTemplateMutation, useUpdateSavedTemplateMutation } from '../../../features/templates/templatesApi';
import { useSaveContentToCmsMutation, useUpdateContentInCmsMutation } from '../../../features/contents/contentsApi';
import { compilePlayerData } from '../../../utils/compileUtils';
import { ProjectCardMenu } from '../../../components/shared/ProjectCardMenu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import toast from 'react-hot-toast';

function Wrapper({ children, additionalClassNames = "" }: React.PropsWithChildren<{ additionalClassNames?: string }>) {
  return (
    <div className={additionalClassNames}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        {children}
      </svg>
    </div>
  );
}

interface Design {
  id: string;
  title: string;
  edited: string;
  imageNode: React.ReactNode;
  rawTemplate?: SavedDesign;
  rawState?: any;
  isSavedToCms?: boolean;
}

function DesignCard({ title, edited, imageNode, onClick, onDelete, onSaveToCms, isSavedToCms }: {
  title: string,
  edited: string,
  imageNode: React.ReactNode,
  onClick?: () => void,
  onDelete?: () => void,
  onSaveToCms?: () => void,
  isSavedToCms?: boolean
}) {
  return (
    <div
      className="content-stretch flex flex-col gap-[14px] items-start min-h-px min-w-px relative shrink-0 group cursor-pointer"
      onClick={onClick}
    >
      <div className="w-full relative">
        {imageNode}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors rounded-[6px]" />
        {isSavedToCms && (
          <div className="absolute top-2 left-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm z-10">
            SAVED TO CMS
          </div>
        )}
      </div>
      <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
        <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 flex-1 min-w-0">
          <p className="font-sans font-medium relative shrink-0 text-[#101928] text-[14px] text-left text-nowrap truncate w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
            {title}
          </p>
          <p className="font-sans font-normal min-w-full relative shrink-0 text-[#667185] text-[13px] w-[min-content]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
            {edited}
          </p>
        </div>
        <ProjectCardMenu
          onDelete={onDelete}
          onSaveToCms={onSaveToCms}
          className="opacity-0 group-hover:opacity-100 transition-opacity z-20"
        />
      </div>
    </div>
  );
}

const formatTimeAgo = (timestamp: number) => {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `Edited ${minutes}m ago`;
  if (hours < 24) return `Edited ${hours}h ago`;
  return `Edited ${days}d ago`;
};

export function AllDesignsView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteSavedTemplateMutation();
  const [saveToCms, { isLoading: isSavingToCms }] = useSaveContentToCmsMutation();
  const [updateCmsContent, { isLoading: isUpdatingCms }] = useUpdateContentInCmsMutation();
  const [updateTemplate] = useUpdateSavedTemplateMutation();
  const [templateToDelete, setTemplateToDelete] = React.useState<string | null>(null);
  const [cmsConfirmTemplate, setCmsConfirmTemplate] = React.useState<Design | null>(null);

  const { data: remoteTemplates, isLoading: isRemoteLoading } = useGetSavedTemplatesQuery();
  const apiDesigns: Design[] = (remoteTemplates?.data || [])
    .filter(t => t.category !== 'Brand Kit')
    .map(template => ({
      id: template._id,
      title: template.name,
      edited: formatTimeAgo(new Date(template.updatedAt).getTime()),
      isSavedToCms: template.isSavedToCms,
      imageNode: (
        <div className="bg-[#f0f2f5] aspect-[272/178] overflow-clip relative rounded-[6px] shrink-0 w-full flex items-center justify-center p-4">
          <img
            alt={template.name}
            className={`absolute inset-0 size-full ${template.thumbnail ? 'object-cover object-top' : 'object-contain p-8 opacity-10'}`}
            src={template.thumbnail || DEFAULT_PLACEHOLDER}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = DEFAULT_PLACEHOLDER;
              target.className = "absolute inset-0 size-full object-contain p-8 opacity-10";
              const label = target.parentElement?.querySelector('.no-preview-label');
              if (label) label.classList.remove('hidden');
            }}
          />
          <div className={`no-preview-label absolute bottom-4 inset-x-0 text-center text-[10px] text-gray-400 font-bold uppercase tracking-widest ${template.thumbnail ? 'hidden' : ''}`}>
            No preview available
          </div>
        </div>
      ),
      rawTemplate: template
    }));

  // Combine remote, local (if unique), and static
  const allDesigns = [...apiDesigns];

  const handleDesignClick = (design: Design) => {
    if (design.id.startsWith('static-')) return;

    if (design.rawTemplate) {
      const template = design.rawTemplate;
      const { id: dataId, ...otherData } = template.data || {};

      dispatch(setSelectedTemplate({
        id: template._id,
        originTemplateId: dataId, // Keep data.id as originTemplateId
        name: template.name,
        previewUrl: template.thumbnail,
        isCustom: true,
        ...otherData // Spread the rest of the data, excluding the old 'id'
      }));
    } else if (design.rawState) {
      const state = design.rawState;
      dispatch(setSelectedTemplate({
        id: state.id,
        originTemplateId: state.originTemplateId,
        name: state.pageName,
        previewUrl: '',
        isCustom: true,
        ...state
      }));
    }

    navigate('/template-editor');
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate(id).unwrap();
      toast.success('Project deleted successfully');
    } catch (err) {
      console.error('Failed to delete template:', err);
      toast.error('Failed to delete project');
    } finally {
      setTemplateToDelete(null);
    }
  };

  const handleSaveToCms = async (design: Design, isNew: boolean = false) => {
    if (!design.rawTemplate) return;

    const template = design.rawTemplate;
    const isReplacing = !isNew && template.cmsContentId;

    const loadingToast = toast.loading(
      isReplacing ? 'Updating CMS entry...' : (isNew ? 'Creating new CMS entry...' : 'Saving to CMS...')
    );

    try {
      // Compile player data
      const playerData = compilePlayerData(template.data);

      const cmsBody = {
        name: template.name,
        thumbnail: template.thumbnail,
        data: template.data,
        player_data: playerData
      };

      let cmsResponse;
      if (isReplacing && template.cmsContentId) {
        // Use PATCH for replacements if we have an ID
        cmsResponse = await updateCmsContent({
          contentId: template.cmsContentId,
          body: cmsBody
        }).unwrap();
      } else {
        // Use POST for new entries
        cmsResponse = await saveToCms(cmsBody).unwrap();
      }

      console.log('CMS Response received:', cmsResponse);

      // Update local status with isSavedToCms and the cmsContentId from response
      const updatePayload = {
        isSavedToCms: true,
        cmsContentId: cmsResponse._id || (cmsResponse as any).id || (typeof cmsResponse === 'string' ? cmsResponse : undefined)
      };

      console.log('Updating local template with payload:', updatePayload);

      const updateResult = await updateTemplate({
        id: template._id,
        data: updatePayload
      }).unwrap();

      console.log('Local template update result:', updateResult);

      toast.success(
        isReplacing ? 'CMS entry updated successfully' : (isNew ? 'Created as new in CMS' : 'Saved to CMS successfully'),
        { id: loadingToast }
      );
    } catch (err) {
      console.error('Failed to save to CMS:', err);
      toast.error('Failed to save to CMS', { id: loadingToast });
    } finally {
      setCmsConfirmTemplate(null);
    }
  };

  const onSaveToCmsClick = (design: Design) => {
    // Robust check: if either the boolean flag or a CMS ID is present, we treat it as already saved
    if (design.isSavedToCms || design.rawTemplate?.cmsContentId) {
      setCmsConfirmTemplate(design);
    } else {
      handleSaveToCms(design);
    }
  };

  return (
    <div className="content-stretch flex flex-col gap-[32px] items-start w-full">
      <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
        <div className="bg-white h-[48px] relative rounded-[12px] shrink-0 w-[358px]" data-name="Search bar">
          <div className="content-stretch flex items-center overflow-clip px-[12px] py-[3px] relative rounded-[inherit] size-full">
            <div className="content-stretch flex items-center relative shrink-0">
              <div className="content-stretch flex gap-[8px] items-center relative flex-1">
                <div className="relative shrink-0 size-[16px]" data-name="search-normal">
                  <Wrapper additionalClassNames="absolute contents inset-0">
                    <g id="search-normal">
                      <path d={svgPaths.p20103e00} id="Vector" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                      <path d={svgPaths.p133f8470} id="Vector_2" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                      <g id="Vector_3" opacity="0"></g>
                    </g>
                  </Wrapper>
                </div>
                <input
                  type="text"
                  placeholder="Search templates..."
                  className="font-sans font-medium leading-[24px] relative flex-1 text-[#101928] text-[13px] outline-none placeholder-[#98a2b3] px-2"
                  style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
                />
              </div>
            </div>
          </div>
          <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[12px]" />
        </div>
        {/* <div
          className="bg-[#30a84b] content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 cursor-pointer hover:bg-[#288e3e] transition-colors"
          data-name="Primary - Sm"
          onClick={() => {
            const newId = `new-${Date.now()}`;
            dispatch(setSelectedTemplate({
              id: newId,
              name: 'New Design',
              previewUrl: '',
              isCustom: true,
              layers: []
            }));
            navigate('/template-editor');
          }}
        >
          <div className="content-stretch flex gap-[4px] items-center relative shrink-0">
            <div className="relative shrink-0 size-[16px]" data-name="add">
              <Wrapper additionalClassNames="absolute contents inset-0">
                <g id="add">
                  <path d="M4 8H12" id="Vector" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                  <path d="M8 12V4" id="Vector_2" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                  <g id="Vector_3" opacity="0"></g>
                </g>
              </Wrapper>
            </div>
            <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              New Design
            </p>
          </div>
        </div> */}
      </div>
      <div className="content-stretch flex flex-col gap-[24px] items-start relative shrink-0 w-full">

        {isRemoteLoading ? (
          <div className="w-full py-20 flex flex-col items-center justify-center gap-4 text-[#667185]">
            <div className="w-8 h-8 border-2 border-[#30A84B] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium">Loading your designs...</p>
          </div>
        ) : allDesigns.length === 0 ? (
          <div className="w-full py-20 flex flex-col items-center justify-center gap-4 text-[#667185]">
            <p className="text-sm font-medium">No designs found. Create your first one!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[24px] w-full">
            {allDesigns.map((design) => (
              <DesignCard
                key={design.id}
                title={design.title}
                edited={design.edited}
                imageNode={design.imageNode}
                isSavedToCms={design.isSavedToCms}
                onClick={() => handleDesignClick(design)}
                onDelete={() => setTemplateToDelete(design.id)}
                onSaveToCms={() => onSaveToCmsClick(design)}
              />
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-sans font-semibold">Are you sure you want to delete this project?</AlertDialogTitle>
            <AlertDialogDescription className="font-sans font-normal">
              This action cannot be undone. This will permanently delete your design from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-sans font-medium">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => templateToDelete && handleDelete(templateToDelete)}
              className="bg-red-600 hover:bg-red-700 text-white font-sans font-medium"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cmsConfirmTemplate} onOpenChange={(open) => !open && setCmsConfirmTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-sans font-semibold">Project already saved to CMS</AlertDialogTitle>
            <AlertDialogDescription className="font-sans font-normal">
              This project has already been saved to the CMS. Would you like to replace the existing entry or create a new one?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="font-sans font-medium mt-0">Cancel</AlertDialogCancel>
            <div className="flex gap-2 w-full sm:w-auto">
              <AlertDialogAction
                onClick={() => cmsConfirmTemplate && handleSaveToCms(cmsConfirmTemplate, true)}
                className="flex-1 sm:flex-none border border-[#30a84b] bg-white text-[#30a84b] hover:bg-[#f0f9f1] font-sans font-medium"
                disabled={isSavingToCms}
              >
                Create New
              </AlertDialogAction>
              <AlertDialogAction
                onClick={() => cmsConfirmTemplate && handleSaveToCms(cmsConfirmTemplate, false)}
                className="flex-1 sm:flex-none bg-[#30a84b] hover:bg-[#288e3e] text-white font-sans font-medium"
                disabled={isSavingToCms || isUpdatingCms}
              >
                Replace
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

